import { BrowserRouter as Router, useLocation, Route, Routes, Navigate } from 'react-router-dom';
import './i18n'; 
import { useTranslation, I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { preloadTranslations } from './translation/preloadTranslations';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from 'antd';
import performanceMonitor from './utils/performanceMonitor';
import Settings from "./components/common/settings";
import Issues from "./components/common/issues";
import NotFound from "./components/common/NotFound";
import Forbidden from "./components/common/Forbidden";
import LoadingSpinner from "./components/common/LoadingSpinner";
import ServerErrorModal from "./components/common/ServerErrorModal";

import Footer1 from "./icons/Footer1.png";
import Footer2 from "./icons/Footer2.png";
import Footer3 from "./icons/Footer3.png";
import Footer4 from "./icons/Footer4.png";

import { AuthProvider, useAuth } from './context/AuthContext';
import { ServerProvider } from './context/ServerContext';
import Navbar from "./components/common/Navbar";
import ScrollToTop from './components/common/ScrollToTop';
import React, { lazy, Suspense, useState, useEffect, useRef, memo } from 'react';

const Login = lazy(() => import('./components/common/Login'));
const ResetPassword = lazy(() => import('./components/common/ResetPassword'));
const Formazione = lazy(() => import('./components/common/formazione'));

const Homepage = lazy(() => import('./components/tutor/homepage'));
const DocList = lazy(() => import('./components/tutor/docList'));
const PatientList = lazy(() => import('./components/tutor/patientList'));

const PatientQuestionnaire = lazy(() => import('./components/tutor/patientSummary'));
const PatientData = lazy(() => import('./components/tutor/patientData'));
const SessionSurvey = lazy(() => import('./components/survey/SurveyComponent'));

const AllegatoPss = lazy(() => import('./components/tutor/allegatoPss'));
const GuidaTool = lazy(() => import('./components/tutor/guidaTool'));

const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const Statistics = lazy(() => import('./components/admin/Statistics'));
const IssueManagement = lazy(() => import('./components/admin/IssueManagement'));
const PasswordPolicyAdmin = lazy(() => import('./components/admin/security/PasswordPolicyAdmin'));

import PatientReport from "./components/tutor/Report";

const { Content, Footer: AntFooter } = Layout;
const MOBILE_BREAKPOINT = 768; 
const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <InternalLoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  } else if (user.role === 'tutor') {
    return <Navigate to="/homepage" replace />;
  }

  return <Navigate to="/login" replace />;
};

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const isProtectionOff = process.env.REACT_PROTECTED_ROUTES === 'OFF';
  if (isProtectionOff) {
    return children;
  }
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <InternalLoadingSpinner />;
  }
  if (!user) {
    localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/forbidden" state={{ from: location }} replace />;
  }

  return children;
};


function AppContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  useEffect(() => {
    performanceMonitor.trackRender('AppContent');
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hideNavbar = location.pathname === "/login";

  const layoutStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    ...(hideNavbar && { height: '100vh', overflow: 'hidden' })
  };

  const contentStyle = {
    display: 'flex',
    flexDirection: 'column',
    ...(hideNavbar 
      ? { 
          height: 'calc(100vh - 80px)', 
          overflow: 'hidden' 
        } 
      : { 
          flex: 1,
          paddingBottom: '20px'
        })
  };

  const footerStyle = {
    padding: 0,
    height: '80px',
    textAlign: 'center',
    backgroundColor: '#fff',
    ...(hideNavbar 
      ? { position: 'fixed', bottom: 0, width: '100%' } 
      : { marginTop: 'auto' }) 
  };

  return (
    <Layout style={hideNavbar ? layoutStyle : {...layoutStyle, height: 'auto'}}>
      {!hideNavbar && <Navbar />}

      <Content style={contentStyle}>
        <div className="pt-2" style={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          ...(hideNavbar 
            ? { overflow: 'hidden' } 
            : { minHeight: '100vh' }) 
        }}> 
          <LoadingHandler>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<RoleBasedRedirect />} />
                <Route path="/forbidden" element={<Forbidden />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/login" element={<Login />} />
                <Route path="/formazione" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <Formazione />
                  </ProtectedRoute>
                } />
                <Route path="/homepage" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <Homepage />
                  </ProtectedRoute>
                } />
                <Route path="/patient-list" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <PatientList />
                  </ProtectedRoute>
                } />
                <Route path="/elenco-discenti" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <DocList />
                  </ProtectedRoute>
                } />
                <Route path="/patient-questionnaire" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <PatientQuestionnaire />
                  </ProtectedRoute>
                } />
                <Route path="/patient-data" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <PatientData />
                  </ProtectedRoute>
                } />
                
                <Route path="formazione/tabella-decreto" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <AllegatoPss />
                  </ProtectedRoute>
                } />

                {/* <Route path="/guida-tool" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <GuidaTool />
                  </ProtectedRoute>
                } /> */}

                <Route path="/patient-report" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <PatientReport />
                  </ProtectedRoute>
                } />
                <Route path="/questionario" element={
                  <ProtectedRoute allowedRoles={['tutor']}>
                    <SessionSurvey />
                  </ProtectedRoute>
                } />
                <Route path="/issues" element={
                  <ProtectedRoute allowedRoles={['tutor', 'admin']}>
                    <Issues />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute allowedRoles={['tutor', 'admin']}>
                    <Settings />
                  </ProtectedRoute>
                } />

                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <UserManagement />
                  </ProtectedRoute>
                } />
                <Route path="/admin/statistics" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Statistics />
                  </ProtectedRoute>
                } />
                <Route path="/admin/issues" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <IssueManagement />
                  </ProtectedRoute>
                } />
                <Route path="/admin/password-policy" element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <PasswordPolicyAdmin />
                  </ProtectedRoute>
                } />
                
                <Route path="*" element={<NotFound />} />

              </Routes>
            </Suspense>
          </LoadingHandler>
        </div>
      </Content>

      <AntFooter style={footerStyle}>
        <div className="footer-icons-row">
          <img 
            src={Footer1} 
            alt="Footer" 
            className='footer-logo'
          />
          <img 
            src={Footer2} 
            alt="Footer" 
            className='footer-logo'
          />
          <img 
            src={Footer3} 
            alt="Footer" 
            className='footer-logo' 
          />
          <img 
            src={Footer4} 
            alt="Footer" 
            className='footer-logo' 
          />
        </div>
      </AntFooter>
    </Layout>
    
    
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, 
      cacheTime: 10 * 60 * 1000, 
      retry: false, 
      refetchOnWindowFocus: false, 
      refetchOnMount: false, 
    },
  },
});

function App() {
  const appInitialized = React.useRef(false);

  useEffect(() => {
    if (!appInitialized.current) {
      performanceMonitor.start('app-init', 'app');
      performanceMonitor.end('app-init', 'app');
      appInitialized.current = true;
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <AuthProvider>
            <ServerProvider>
              <AppContent />
              <ServerErrorModal />
            </ServerProvider>
          </AuthProvider>
        </Router>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

const InternalLoadingSpinner = memo(() => {
  React.useEffect(() => {
    performanceMonitor.trackRender('LoadingSpinner');
  });
  return React.createElement(
    'div',
    { className: 'loading-spinner-container' },
    React.createElement('div', { className: 'loading-spinner' })
  );
});

const LoadingHandler = memo(({ children }) => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const timerRef = useRef(null);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      performanceMonitor.start(location.pathname, 'route');

      setLoading(true);
      previousPathRef.current = location.pathname;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setLoading(false);
        timerRef.current = null;

        performanceMonitor.end(location.pathname, 'route');
      }, 200);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [location.pathname]);

  return (
    <React.Fragment>
      {loading && <InternalLoadingSpinner />}
      {children}
    </React.Fragment>
  );
});

export default App;